/**
 * KioskRegisterButton
 * 
 * Renders an admin-only control that pins this physical device into Kiosk Mode.
 * Once registered, the kiosk_mode flag is written to LocalStorage and persists
 * across hard refreshes. The device will route to the PIN-pad on every load.
 *
 * Required role: manager, gm, ops, or super_admin.
 * Place this component inside the Settings or Users admin panel.
 */
import React, { useState } from 'react';
import { Monitor, MonitorOff, AlertTriangle, CheckCircle2, Loader2 } from 'lucide-react';
import { useAuthStore, ROLES } from '../../lib/auth';
import { useKioskStore } from '../../lib/kiosk';
import { venueAPI } from '../../lib/api';

const ALLOWED_ROLES = [ROLES.SUPER_ADMIN, ROLES.OPS, ROLES.GM, ROLES.MANAGER];

export default function KioskRegisterButton() {
  const { user } = useAuthStore();
  const { isKioskDevice, kioskVenueName, register, deregister, deviceId } = useKioskStore();

  const [showModal, setShowModal]     = useState(false);
  const [venues, setVenues]           = useState([]);
  const [selectedVenue, setSelected]  = useState('');
  const [loading, setLoading]         = useState(false);
  const [step, setStep]               = useState('idle'); // idle | confirm | success | deregister
  const [error, setError]             = useState('');

  // Guard: only allowed roles see this button
  if (!user || !ALLOWED_ROLES.includes(user.role)) return null;

  const openModal = async () => {
    if (isKioskDevice) {
      setStep('deregister');
      setShowModal(true);
      return;
    }
    setLoading(true);
    setError('');
    try {
      const res = await venueAPI.list();
      setVenues(res.data?.venues || res.data || []);
      setSelected(res.data?.venues?.[0]?.id || res.data?.[0]?.id || '');
    } catch {
      setError('Could not load venues. Check your connection.');
    } finally {
      setLoading(false);
      setStep('confirm');
      setShowModal(true);
    }
  };

  const handleRegister = () => {
    if (!selectedVenue) { setError('Please select a venue.'); return; }
    const venueName = venues.find((v) => v.id === selectedVenue)?.name || '';
    const fp = register({ venueId: selectedVenue, venueName, registeredBy: user.email });
    setStep('success');
    console.info(`[RTB Kiosk] Device registered. Fingerprint: ${fp}`);
  };

  const handleDeregister = () => {
    deregister();
    setShowModal(false);
    setStep('idle');
    // Reload so the app re-evaluates the routing logic
    window.location.reload();
  };

  const close = () => { setShowModal(false); setStep('idle'); setError(''); };

  return (
    <>
      {/* Trigger button */}
      <button
        onClick={openModal}
        className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold transition-colors ${
          isKioskDevice
            ? 'bg-orange-600/20 text-orange-400 border border-orange-600/40 hover:bg-orange-600/30'
            : 'bg-[#27272A] text-[#A1A1AA] border border-[#3F3F46] hover:bg-[#3F3F46] hover:text-white'
        }`}
      >
        {isKioskDevice ? <MonitorOff className="w-4 h-4" /> : <Monitor className="w-4 h-4" />}
        {isKioskDevice ? 'Kiosk Registered' : 'Register as Kiosk'}
      </button>

      {/* Modal overlay */}
      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4">
          <div className="bg-[#18181B] border border-[#3F3F46] rounded-2xl w-full max-w-md p-6 shadow-2xl">

            {/* ── Deregister confirmation ── */}
            {step === 'deregister' && (
              <>
                <div className="flex items-center gap-3 mb-4">
                  <div className="p-2 rounded-lg bg-red-900/30">
                    <AlertTriangle className="w-6 h-6 text-red-400" />
                  </div>
                  <h2 className="text-white text-lg font-bold">Deregister Kiosk</h2>
                </div>
                <p className="text-[#A1A1AA] text-sm mb-2">
                  This will remove the Kiosk Mode flag from this device. After deregistering, the
                  app will route to the <strong className="text-white">Staff Portal login</strong> instead
                  of the PIN pad.
                </p>
                <div className="bg-[#27272A] rounded-lg p-3 mb-4 text-xs text-[#71717A] font-mono">
                  <span className="text-[#52525B]">Device: </span>{deviceId}<br />
                  <span className="text-[#52525B]">Venue: </span>{kioskVenueName}
                </div>
                <div className="flex gap-3">
                  <button onClick={close} className="flex-1 py-2 rounded-lg border border-[#3F3F46] text-[#A1A1AA] hover:text-white text-sm">Cancel</button>
                  <button onClick={handleDeregister} className="flex-1 py-2 rounded-lg bg-red-700 hover:bg-red-600 text-white font-semibold text-sm">
                    Deregister
                  </button>
                </div>
              </>
            )}

            {/* ── Venue selection / confirm ── */}
            {step === 'confirm' && (
              <>
                <div className="flex items-center gap-3 mb-4">
                  <div className="p-2 rounded-lg bg-[#FF6600]/20">
                    <Monitor className="w-6 h-6 text-[#FF6600]" />
                  </div>
                  <h2 className="text-white text-lg font-bold">Register as Kiosk</h2>
                </div>
                <p className="text-[#A1A1AA] text-sm mb-4">
                  This locks the device into <strong className="text-white">Terminal Mode</strong>.
                  Staff will log in via numeric PIN. The flag persists until an admin deregisters it.
                </p>

                <label className="block text-[#A1A1AA] text-xs font-semibold uppercase mb-1">
                  Assign Venue
                </label>
                {loading ? (
                  <div className="flex items-center gap-2 text-[#A1A1AA] text-sm py-3">
                    <Loader2 className="w-4 h-4 animate-spin" /> Loading venues…
                  </div>
                ) : (
                  <select
                    value={selectedVenue}
                    onChange={(e) => setSelected(e.target.value)}
                    className="w-full bg-[#27272A] border border-[#3F3F46] text-white rounded-lg px-3 py-2 text-sm mb-4 focus:outline-none focus:ring-2 focus:ring-[#FF6600]"
                  >
                    <option value="">— Select a venue —</option>
                    {venues.map((v) => (
                      <option key={v.id} value={v.id}>{v.name}</option>
                    ))}
                  </select>
                )}

                {error && <p className="text-red-400 text-sm mb-3">{error}</p>}

                <div className="flex gap-3">
                  <button onClick={close} className="flex-1 py-2 rounded-lg border border-[#3F3F46] text-[#A1A1AA] hover:text-white text-sm">Cancel</button>
                  <button
                    onClick={handleRegister}
                    disabled={!selectedVenue}
                    className="flex-1 py-2 rounded-lg bg-[#FF6600] hover:bg-[#e55a00] disabled:opacity-40 text-white font-semibold text-sm"
                  >
                    Confirm & Register
                  </button>
                </div>
              </>
            )}

            {/* ── Success ── */}
            {step === 'success' && (
              <>
                <div className="flex items-center gap-3 mb-4">
                  <div className="p-2 rounded-lg bg-green-900/30">
                    <CheckCircle2 className="w-6 h-6 text-green-400" />
                  </div>
                  <h2 className="text-white text-lg font-bold">Kiosk Registered</h2>
                </div>
                <p className="text-[#A1A1AA] text-sm mb-4">
                  This device is now in <strong className="text-white">Terminal Mode</strong>.
                  The app will reload and route to the PIN-pad screen.
                </p>
                <button
                  onClick={() => { close(); window.location.reload(); }}
                  className="w-full py-2 rounded-lg bg-[#FF6600] hover:bg-[#e55a00] text-white font-semibold text-sm"
                >
                  Reload & Start Kiosk
                </button>
              </>
            )}

          </div>
        </div>
      )}
    </>
  );
}
